// Вкладка «Связи»: с чем карточка связана и кто ссылается на неё.
//
// ЗАЧЕМ ОБРАТНЫЕ СВЯЗИ. Прямые связи видно и в полях: у сделки написано, какая компания и
// какое судно. Обратной стороны не видно нигде, а работают чаще именно с ней: открыли
// компанию — нужны её сделки, счета и договоры; открыли судно — его акты и проверки.
// Без обратной стороны карточка остаётся анкетой, и человек уходит искать то же самое
// фильтром в списке.
//
// ПОЧЕМУ ЗАГРУЖАЕТСЯ ПО ОТКРЫТИЮ ВКЛАДКИ. Обратных связей у компании три-четыре, и каждая
// — отдельный запрос. Тянуть их вместе с карточкой значит платить за них всегда, а
// открывают их не всегда.

import { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { ИсточникКарточек, СтрокаСвязи } from './source'
import { текстЗначения, ярлык } from './values'
import type { ОписаниеКарточки, Сущность, Значения } from './types'

interface Props {
  описание: ОписаниеКарточки
  значения: Значения
  id: string
  источник: ИсточникКарточек
  описаниеСущности: (с: Сущность) => ОписаниеКарточки | undefined
}

export function LinksTab({ описание, значения, id, источник, описаниеСущности }: Props) {
  const прямые = описание.поля.filter((п) => п.вид === 'связь')

  return (
    <>
      <div className="card">
        <h2>Прямые связи</h2>
        <div className="hint">
          Поля самой карточки: на кого она ссылается. Правятся на вкладке «Основное».
        </div>
        {прямые.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Прямых связей нет</div>
            <div className="empty-hint">У этой сущности нет полей-ссылок на другие карточки.</div>
          </div>
        ) : (
          <dl className="dl">
            {прямые.map((п) => {
              const я = ярлык(значения, п.код)
              const о = я ? описаниеСущности(я.сущность) : undefined
              return (
                // Пара dt/dd — прямые дети .dl: это сетка в две колонки, и обёртка вокруг
                // пары сломала бы её (обёртка стала бы одной ячейкой на обе колонки).
                <Fragment key={п.код}>
                  <dt>{п.подпись}</dt>
                  <dd>
                    {я && о ? <Link to={о.путь(я.id)}>{я.название}</Link> : null}
                    {я && !о ? я.название : null}
                    {!я ? <span className="muted">не привязано</span> : null}
                    {я?.подсказка && <span className="muted"> · {я.подсказка}</span>}
                  </dd>
                </Fragment>
              )
            })}
          </dl>
        )}
      </div>

      {описание.обратные.map((о) => (
        <ОбратнаяТаблица
          key={о.код}
          описание={описание}
          связь={о}
          id={id}
          источник={источник}
          описаниеСущности={описаниеСущности}
        />
      ))}
    </>
  )
}

function ОбратнаяТаблица({
  описание, связь, id, источник, описаниеСущности,
}: {
  описание: ОписаниеКарточки
  связь: ОписаниеКарточки['обратные'][number]
  id: string
  источник: ИсточникКарточек
  описаниеСущности: (с: Сущность) => ОписаниеКарточки | undefined
}) {
  const [строки, setСтроки] = useState<СтрокаСвязи[] | null>(null)
  const [отказ, setОтказ] = useState<string | null>(null)

  useEffect(() => {
    let брошен = false
    setСтроки(null)
    setОтказ(null)
    источник.обратные(описание.сущность, id, связь.код)
      .then((р) => { if (!брошен) setСтроки(р) })
      .catch((е: Error) => { if (!брошен) setОтказ(е.message) })
    return () => { брошен = true }
  }, [источник, описание.сущность, id, связь.код])

  const чужое = описаниеСущности(связь.сущность)

  return (
    <div className="card">
      <h2>{связь.заголовок}</h2>
      {отказ && (
        <div className="notice err">
          Связанные записи не пришли: {отказ}. Пустая таблица здесь означала бы «связей нет»,
          поэтому мы её не показываем.
        </div>
      )}
      {!отказ && строки === null && <div className="skeleton" />}
      {!отказ && строки !== null && строки.length === 0 && (
        <div className="empty">
          <div className="empty-title">Связанных записей нет</div>
          <div className="empty-hint">
            Ни одна карточка «{чужое?.много ?? связь.сущность}» на эту не ссылается.
          </div>
        </div>
      )}
      {!отказ && строки !== null && строки.length > 0 && (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                {связь.колонки.map((к) => (
                  <th key={к.код} className={к.число ? 'num' : undefined}>{к.подпись}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {строки.map((с) => (
                <tr key={с.id}>
                  {связь.колонки.map((к, i) => {
                    const поле = чужое?.поля.find((п) => п.код === к.код)
                    const текст = поле
                      ? текстЗначения(поле, с.значения[к.код], с.значения)
                      : String(с.значения[к.код] ?? '—')
                    return (
                      <td key={к.код} data-label={к.подпись} className={к.число ? 'num' : undefined}>
                        {i === 0 && чужое
                          ? <Link to={чужое.путь(с.id)}>{текст}</Link>
                          : текст}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
